import Button from "@components/Button";
import ContactDialog from "@components/contact-dialog";
import FormFooter from "@components/footer/components/form";
import { styled, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import Link from "next/link";
import React, { useState } from "react";
import { CPagesPath } from "shared/models/const/pages-path";

function RemaxV2ActionSections() {
  const t = useTranslations("home.remaxV2");
  const [openContact, setOpenContact] = useState(false);

  return (
    <>
      <ActionBand className="tone-red agent-band">
        <BandInner className="agent-layout">
          <BandCopy>
            <BandTitle>{t("agent.title")}</BandTitle>
            <BandText>{t("agent.description")}</BandText>
            <Link href={CPagesPath.es.join} passHref>
              <Button component="a" bg="--color-brand-blue-main">
                {t("agent.button")}
              </Button>
            </Link>
          </BandCopy>
          <SoftEdgeImageFrame className="blend-red agent-photo-frame">
            <BandImage
              className="agent-photo soft-edge-photo"
              src="/images/v2/pic/image-02.jpeg"
              alt={t("agent.title")}
            />
          </SoftEdgeImageFrame>
        </BandInner>
      </ActionBand>

      <ActionBand className="tone-cream">
        <BandInner className="reverse">
          <SoftEdgeImageFrame className="blend-cream">
            <BandImage
              className="soft-edge-photo"
              src="/images/image-optimize/franchises.webp"
              alt={t("franchise.title")}
            />
          </SoftEdgeImageFrame>
          <BandCopy>
            <BandTitle>{t("franchise.title")}</BandTitle>
            <BandText>{t("franchise.description")}</BandText>
            <Link href={CPagesPath.es.franchises} passHref>
              <Button component="a" bg="--color-brand-red-main">
                {t("franchise.button")}
              </Button>
            </Link>
          </BandCopy>
        </BandInner>
      </ActionBand>

      <ActionBand className="tone-blue">
        <BandInner>
          <BandCopy>
            <BandTitle>{t("blog.title")}</BandTitle>
            <BandText>{t("blog.description")}</BandText>
            <Button
              component="a"
              href={CPagesPath.blog}
              target="_blank"
              rel="noopener noreferrer"
              bg="--color-brand-red-main"
            >
              {t("blog.button")}
            </Button>
          </BandCopy>
          <BlogFormColumn>
            <FormFooter />
          </BlogFormColumn>
        </BandInner>
      </ActionBand>

      <ActionBand className="tone-white">
        <BandInner className="reverse">
          <SoftEdgeImageFrame className="blend-white">
            <BandImage
              className="soft-edge-photo"
              src="/images/image-optimize/franchise-agent-women.webp"
              alt={t("contact.title")}
            />
          </SoftEdgeImageFrame>
          <BandCopy>
            <BandTitle>{t("contact.title")}</BandTitle>
            <BandText>{t("contact.description")}</BandText>
            <Button
              type="button"
              onClick={() => setOpenContact(true)}
              bg="--color-brand-blue-main"
            >
              {t("contact.button")}
            </Button>
          </BandCopy>
        </BandInner>
      </ActionBand>

      <ContactDialog
        open={openContact}
        onClose={() => setOpenContact(false)}
        classNameModalButton="enviar-formulario-contacto-home"
      />
    </>
  );
}

export default RemaxV2ActionSections;

const ActionBand = styled("section")`
  width: 100%;
  padding: 88px 6vw;
  position: relative;
  overflow: hidden;

  &.tone-red {
    background:
      radial-gradient(
        circle at 82% 8%,
        rgba(0, 67, 255, 0.24) 0%,
        rgba(0, 67, 255, 0) 30%
      ),
      linear-gradient(180deg, var(--color-brand-red-main) 0%, #d71108 100%);
    color: var(--color-text-white);
  }

  &.agent-band {
    padding-top: 72px;
  }

  &.tone-blue {
    background-color: var(--color-brand-blue-main);
    color: var(--color-text-white);
  }

  &.tone-cream {
    background-color: var(--color-brand-cream-primary);
    color: var(--color-text-black);
  }

  &.tone-white {
    background-color: var(--color-brand-bg-secondary);
    color: var(--color-text-black);
  }

  @media screen and (max-width: 768px) {
    padding: 56px 20px;
  }
`;

const BandInner = styled("div")`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 500px);
  gap: 56px;
  align-items: center;

  &.reverse {
    grid-template-columns: minmax(280px, 500px) minmax(0, 1fr);
  }

  &.agent-layout {
    grid-template-columns: minmax(0, 0.96fr) minmax(340px, 560px);
    gap: 64px;
  }

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;

    &.reverse {
      grid-template-columns: 1fr;
    }

    &.agent-layout {
      grid-template-columns: 1fr;
      gap: 34px;
    }
  }
`;

const BandCopy = styled("div")`
  max-width: 620px;

  @media screen and (max-width: 900px) {
    max-width: 100%;
    text-align: center;
  }
`;

const BandTitle = styled(Typography)`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 18px;

  @media screen and (max-width: 768px) {
    font-size: 1.55rem;
  }
`;

const BandText = styled(Typography)`
  font-size: 1rem;
  line-height: 1.75;
  margin-bottom: 30px;
  max-width: 590px;

  @media screen and (max-width: 900px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const SoftEdgeImageFrame = styled("div")`
  --soft-edge-bg: var(--color-brand-bg-secondary);
  --soft-edge-rgb: 255, 255, 255;
  width: 100%;
  aspect-ratio: 4 / 3;
  position: relative;
  overflow: hidden;
  background-color: transparent;
  border-radius: 42px;

  &.agent-photo-frame {
    aspect-ratio: 16 / 10;
  }

  &.blend-cream {
    --soft-edge-bg: var(--color-brand-cream-primary);
    --soft-edge-rgb: 247, 245, 238;
  }

  &.blend-red {
    --soft-edge-bg: var(--color-brand-red-main);
    --soft-edge-rgb: 255, 18, 0;
  }

  &.blend-white {
    --soft-edge-bg: var(--color-brand-bg-secondary);
    --soft-edge-rgb: 255, 255, 255;
  }

  &::after {
    content: none;
  }

  @media screen and (max-width: 900px) {
    border-radius: 34px;

    &.agent-photo-frame {
      aspect-ratio: 1 / 1.08;
      max-height: 420px;
    }
  }
`;

const BandImage = styled("img")`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 0 90px 0 90px;

  &.agent-photo {
    aspect-ratio: 16 / 10;
    object-position: 16% center;
    border-radius: 42px;
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.24);
  }

  &.soft-edge-photo {
    height: 100%;
    aspect-ratio: auto;
    border-radius: 0;
    box-shadow: none;
    display: block;
    -webkit-mask-image: radial-gradient(
        ellipse at center,
        #000 0%,
        #000 44%,
        rgba(0, 0, 0, 0.86) 56%,
        rgba(0, 0, 0, 0.36) 70%,
        transparent 86%
      ),
      linear-gradient(
        90deg,
        transparent 0%,
        #000 22%,
        #000 78%,
        transparent 100%
      ),
      linear-gradient(
        180deg,
        transparent 0%,
        #000 22%,
        #000 78%,
        transparent 100%
      );
    mask-image: radial-gradient(
        ellipse at center,
        #000 0%,
        #000 44%,
        rgba(0, 0, 0, 0.86) 56%,
        rgba(0, 0, 0, 0.36) 70%,
        transparent 86%
      ),
      linear-gradient(
        90deg,
        transparent 0%,
        #000 22%,
        #000 78%,
        transparent 100%
      ),
      linear-gradient(
        180deg,
        transparent 0%,
        #000 22%,
        #000 78%,
        transparent 100%
      );
    -webkit-mask-composite: source-in, source-in;
    mask-composite: intersect;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
  }

  @media screen and (max-width: 900px) {
    max-height: 360px;

    &.agent-photo {
      aspect-ratio: 1 / 1.08;
      max-height: 420px;
      object-position: 10% center;
      border-radius: 34px;
    }

    &.soft-edge-photo {
      max-height: none;
    }
  }
`;

const BlogFormColumn = styled("div")`
  width: 100%;
  max-width: 520px;
  margin-left: auto;

  @media screen and (max-width: 900px) {
    max-width: 100%;
    margin: 0;
  }
`;
